import React, { useMemo } from 'react';
import styled from 'styled-components';
import AlertCircleIcon from '../../../../../generic/icons/IconLib/AlertCircleIcon';
import ClockIcon from '../../../../../generic/icons/IconLib/ClockIcon';
import HashIcon from '../../../../../generic/icons/IconLib/HashIcon';
import InvoiceIcon from '../../../../../generic/icons/IconLib/InvoiceIcon';
import PackageBoxIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon';
import PalletIcon from '../../../../../generic/icons/IconLib/PalletIcon';
import ScaleIcon from '../../../../../generic/icons/IconLib/ScaleIcon';
import TruckIcon from '../../../../../generic/icons/IconLib/TruckIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { IImageObjectRef } from '../../../../../modules/API/Images/models/imageObject';
import { buildOrderImageRef } from '../helpers/imageRef';
import {
    ICustomerItem,
    IOrderItem,
} from '../../../../../modules/API/Drivers/models/kavData';
import { aggregateOrders } from '../helpers/aggregate';
import { substring } from '../helpers/kavAction';
import { EDIT_LOCKED_TITLE } from '../helpers/dateLock';
import {
    Actions,
    CardShell,
    DriversTag,
    ChipStrip,
    IconSlot,
    MetricChip,
    NeutralBtn,
    PrimaryName,
} from './DriversCardParts';
import OrderTile from './OrderTile';

const CUSTOMER_ID_COLOR = '#8CC53D';
const STATE_DONE_COLOR = '#22C55E';
const STATE_PENDING_COLOR = '#F59E0B';
const STATE_FAILED_COLOR = '#EF4444';

interface Props {
    customer: ICustomerItem;
    areaID: number;
    isOffice: boolean;
    groupColorOf: (desc: string | null | undefined) => string | undefined;
    onPackageOrder: (customer: ICustomerItem) => void;
    onConfirmDelivery: (customer: ICustomerItem) => void;
    onSelectProblem: (customer: ICustomerItem) => void;
    onOpenPallets: (order: IOrderItem, customer: ICustomerItem) => void;
    onPrint: (deliveryNum: number, deliverySub: number) => void;
    onPhoto: (ref: IImageObjectRef) => void;
    isPhotoUploaded: (ref: IImageObjectRef) => boolean;
    /** Прошедшая дата для WORKER — пакеты, подтверждение, проблемы и фото закрыты. */
    isEditLocked?: boolean;
}

const CustomerCard: React.FC<Props> = ({
    customer,
    isOffice,
    groupColorOf,
    onPackageOrder,
    onConfirmDelivery,
    onSelectProblem,
    onOpenPallets,
    onPrint,
    onPhoto,
    isPhotoUploaded,
    isEditLocked = false,
}) => {
    const { Orders, Type, PO_Num, PD_Num, LastAction } = customer;

    const withTeuda = useMemo(
        () => Orders.filter((order) => order.Delivery_Num !== 0),
        [Orders],
    );
    const qtyWithoutTeuda = Orders.length - withTeuda.length;
    const isReturn = Type === 1;
    const isDelivered = withTeuda.length > 0 && withTeuda.every((e) => e.LastAction.Action === 1);
    const isFailed = withTeuda.length > 0 && withTeuda.every((e) => e.LastAction.Action > 1);
    const isExistTeudot =
        (isReturn && Orders.length > 0) || (Orders.length > 0 && withTeuda.length > 0);
    const isPackageDone = PD_Num !== 0 || PO_Num !== 0;
    const isPackageTouched = LastAction.Action !== 0;
    const isExistProblem = Orders.some((e) => e.IsProblem);
    // Флаг с сервера — после загрузки дерева; локальная отметка — сразу после аплоада.
    const hasOrderPhoto = (order: IOrderItem) => {
        if (order.HasPhotoOrComment) return true;
        const ref = buildOrderImageRef(order);
        return ref !== null && isPhotoUploaded(ref);
    };

    const { weight, pallets } = useMemo(() => aggregateOrders(Orders), [Orders]);

    const actionTime = useMemo(() => {
        if (LastAction.Action !== 0 || LastAction.LogTime !== '') {
            return substring(LastAction.LogTime);
        }
        const found = Orders.find(
            (e) => e.LastAction.Action !== 0 || e.LastAction.LogTime !== '',
        );
        return found ? substring(found.LastAction.LogTime) : '';
    }, [LastAction, Orders]);

    return (
        <CardShell>
            <CardRow
                $columns='minmax(0, 1fr) auto'
                $mobileColumns='minmax(0, 1fr) auto'
                $isLast
                $hover={false}
                $minHeight='auto'
            >
                <CardRow.Cell>
                    <CardRow.Line $gap='0.5rem'>
                        <IconSlot $muted>
                            <UserIcon size={16} />
                        </IconSlot>
                        <PrimaryName>{customer.Customer_Name?.trim()}</PrimaryName>
                        <DriversTag variant='tinted' color={CUSTOMER_ID_COLOR}>
                            {customer.CustomerID}
                        </DriversTag>
                    </CardRow.Line>
                </CardRow.Cell>

                <CardRow.Cell $align='end'>
                    <Actions>
                        <NeutralBtn
                            type='button'
                            $active={isPackageDone}
                            $danger={!isPackageDone}
                            disabled={isEditLocked}
                            onClick={() => onPackageOrder(customer)}
                            aria-label='אריזות'
                            title={isEditLocked ? EDIT_LOCKED_TITLE : 'אריזות'}
                        >
                            {PD_Num !== 0 ? <InvoiceIcon /> : <PackageBoxIcon />}
                        </NeutralBtn>

                        {isExistTeudot && (
                            <NeutralBtn
                                type='button'
                                $active={isDelivered}
                                $danger={!isDelivered}
                                disabled={!isOffice || isEditLocked}
                                onClick={() => onConfirmDelivery(customer)}
                                aria-label='אישור מסירה'
                                title={isEditLocked ? EDIT_LOCKED_TITLE : 'אישור מסירה'}
                            >
                                <TruckIcon />
                            </NeutralBtn>
                        )}

                        {isExistProblem && (
                            <NeutralBtn
                                type='button'
                                disabled={isEditLocked}
                                onClick={() => onSelectProblem(customer)}
                                aria-label='איסוף החזרה'
                                title={isEditLocked ? EDIT_LOCKED_TITLE : 'איסוף החזרה'}
                            >
                                <AlertCircleIcon />
                            </NeutralBtn>
                        )}
                    </Actions>
                </CardRow.Cell>
            </CardRow>

            <ChipStrip>
                <MetricChip icon={<PalletIcon />} value={pallets.toFixed(2)} />
                <MetricChip icon={<ScaleIcon />} value={Math.round(weight)} unit='ק"ג' />
                {actionTime && <MetricChip icon={<ClockIcon />} value={actionTime} />}
                {qtyWithoutTeuda > 0 && (
                    <MetricChip
                        icon={<HashIcon />}
                        value={qtyWithoutTeuda}
                        label='ללא תעודה'
                    />
                )}
                {isReturn && (
                    <DriversTag variant='tinted' color={STATE_DONE_COLOR}>
                        רכש
                    </DriversTag>
                )}
                {isDelivered && (
                    <DriversTag variant='tinted' color={STATE_DONE_COLOR}>
                        נמסר
                    </DriversTag>
                )}
                {isFailed && (
                    <DriversTag variant='tinted' color={STATE_FAILED_COLOR}>
                        לא נמסר
                    </DriversTag>
                )}
                {isOffice && isPackageTouched && LastAction.Action > 1 && (
                    <DriversTag variant='tinted' color={STATE_PENDING_COLOR}>
                        {LastAction.ActionDescription?.trim()}
                    </DriversTag>
                )}
            </ChipStrip>

            {Orders.length > 0 && (
                <TileStrip>
                    {Orders.map((order) => (
                        <OrderTile
                            key={`${order.OrderID}-${order.Delivery_Num}-${order.Delivery_Sub}`}
                            order={order}
                            groupColor={groupColorOf(order.GroupDesc)}
                            onOpenPallets={(item) => onOpenPallets(item, customer)}
                            onPrint={onPrint}
                            onPhoto={onPhoto}
                            hasPhoto={hasOrderPhoto(order)}
                            isPhotoLocked={isEditLocked}
                        />
                    ))}
                </TileStrip>
            )}
        </CardShell>
    );
};

export default CustomerCard;

const TileStrip = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(10.5rem, 1fr));
    gap: 0.375rem;
    padding: 0 1.25rem 0.75rem;
    direction: rtl;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: repeat(auto-fill, minmax(6.25rem, 1fr));
        padding: 0 0.5rem 0.5rem;
    }
`;
